import '../../App.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { useState, useEffect, useRef, useCallback } from 'react';
import React from 'react';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import '@fortawesome/fontawesome-free/css/all.min.css';
import * as Icon from 'react-feather';
import MainStructure from '../../components/general/MainStructure';
import PostedPost from '../components/PostedPost';
import myImage from '../../assets/empty-box-2.png';

 
function ContentCenter() {
  const navigate = useNavigate();
  const { token } = useAuth();

  const [expandedPosts, setExpandedPosts] = useState({});

  const toggleExpanded = (postId) => {
  setExpandedPosts(prev => ({
    ...prev,
    [postId]: !prev[postId],
  }));
};

const [menuOpenId, setMenuOpenId] = useState(null);


const handleUsePost = (postId) => {
  navigate(`/write-post/?content-center-post-id=${postId}`);
  setMenuOpenId(null); // close the dropdown
};


  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null); // paging.before
  const [hasMore, setHasMore] = useState(true);

  // Sentinel ref for IntersectionObserver
  const loadMoreRef = useRef(null);

  /**
   * Fetch a page of posts. When cursor is null, grabs the first page.
   */
  const fetchPosts = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);

    try {
      const url = new URL(`https://api.schedpilot.com/wp-json/custom/v1/linkedin-posts`);
      url.searchParams.append('per_page', '10');
      if (nextCursor) url.searchParams.append('before', nextCursor);

      const res = await fetch(url.toString(), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      setPosts(prev => [...prev, ...data.items]);
      setNextCursor(data.paging.next_before);
      setHasMore(data.has_more);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error loading posts');
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, nextCursor, token]);

  useEffect(() => {
  const handleClickOutside = () => setMenuOpenId(null);
  document.addEventListener('click', handleClickOutside);
  return () => document.removeEventListener('click', handleClickOutside);
}, []);


  // Initial load
  useEffect(() => {
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // IntersectionObserver to trigger loadMore when sentinel appears
  useEffect(() => {
    if (!hasMore) return;
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) fetchPosts();
      },
      { rootMargin: '200px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchPosts, hasMore]);

  /**
   * Render helpers
   */
const renderCard = post => {
  const isExpanded = expandedPosts[post.id];
  const maxChars = 300;

  const fullText = post.text?.replace(/\\n/g, '\n') || '';
  const shouldTruncate = fullText.length > maxChars;
  const displayedText = isExpanded || !shouldTruncate ? fullText : fullText.slice(0, maxChars) + '...';

  return (
    <div className="card h-100 shadow-sm" key={post.id}>
      <div className="card-body p-3">
        <div className="d-flex align-items-start mb-2">
          <img
            src={post.avatar || myImage}
            className="rounded-circle me-2"
            alt="avatar"
            width="40"
            height="40"
          />
          <div className="flex-grow-1">
            <h6 className="mb-0 fw-bold">{post.author}</h6>
            <small className="text-muted">{new Date(post.date_added).toLocaleDateString()}</small>
          </div>
          <div className="position-relative"   onClick={(e) => e.stopPropagation()} >
  <button
    className="btn btn-sm p-1"
    onClick={() =>
      setMenuOpenId(prev => (prev === post.id ? null : post.id))
    }
  >
    <Icon.MoreVertical size={18} />
  </button>

  {menuOpenId === post.id && (
    <div className="position-absolute bg-white border rounded shadow-sm" style={{ right: 0, top: '100%', zIndex: 10 }}>
      <a href="#"
        className="p-3 dropdown-item small"
        onClick={() => handleUsePost(post.id)}
      >
        Use in new post
      </a>
    </div>
  )}
</div>

        </div>

        <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>
          {displayedText}
        </p>

        {shouldTruncate && (
          <button
            onClick={() => toggleExpanded(post.id)}
            className="btn btn-link p-0 small"
            style={{ textDecoration: 'none' }}
          >
            {isExpanded ? 'View less' : 'View more'}
          </button>
        )}

        {post.main_image && (
          <img src={post.main_image} alt="post" className="w-100 rounded" />
        )}

        <div className="d-flex justify-content-start mt-3 text-muted small">
          {post.social_proof !== null && (
            <span className="me-3">
              <Icon.ThumbsUp size={14} className="me-1" />
              {post.social_proof}
            </span>
          )}
          {post.comments !== null && (
            <span>
              <Icon.MessageCircle size={14} className="me-1" />
              {post.comments}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};


  return (
    <MainStructure activeItem="content-center">
      <div className="container-fluid p-0"  >
        <div className="d-flex">
          <div className="flex-grow-1">
            {/* Header */}
            <div className="d-flex mb-2">
              <div>
                <h1 className="h3 mb-0 fw-bold">Content Center</h1>
                <p className="text-muted mb-0">Find posts you have gathered and write your own</p>
              </div>
            </div>

            {/* Posts list */}
            {posts.length === 0 && !loading && !error && (
              <div className="text-center py-5">
                <img src={myImage} alt="empty" style={{ width: 140 }} className="opacity-75 mb-3" />
                <p className="mb-0 text-muted">No posts yet. Start adding some!</p>
              </div>
            )}

<div className="masonry">
  {posts.map(post => (
    <div className="masonry-item" key={post.id}>
      {renderCard(post)}
    </div>
  ))}
</div>



            {/* Loading / error / sentinel */}
            {error && <div className="alert alert-danger">{error}</div>}
            {loading && <LoadingSpinner />}
            <div ref={loadMoreRef} />
            {!hasMore && posts.length > 0 && (
              <p className="text-center text-muted small my-3">You\'ve reached the end.</p>
            )}
          </div>
        </div>
      </div>
    </MainStructure>
  );
}

export default ContentCenter;
